import { defineStore } from 'pinia'
import type { MenuOperation, MenuRight, ModuleMenu, ActiveModule } from '../types/menu'

const STORAGE_KEY = 'mop_auth_state'

interface User {
  id: number;
  organizationId: number;
  roleId: number;
  email: string;
  name: string;
  branchId: number;  // Added for user's branch
  deptRoleMapId: number;
  isAdmin: boolean;  // Added for admin status
}

export interface AuthState {
  token: string | null;
  user: User | null;
  companyId: number;
  financialyearId: number;
  financialyearstartdate: any;
  financialyearenddate: any;
  entityId: number;
  taskEntityId: number; // Added for task-specific entity ID
  isCommon: number;
  voucherType: number;
  roleId: number;
  deptRoleMapId: number;
  OrganizationId: number;
  branchId: number;
  activeModule: ActiveModule | null;
  companyListLength: number;
  moceroUIUsersData: any;
  moceroUIRolesData: any;
  userId: number;    // Added for user ID
  userName: string;  // Added for user name
  isAdmin: boolean;  // Added for admin status
  isDepartmentHead: boolean; // Added for department head status
  SubDepartmentId: number;
}

function loadInitialState(): AuthState {
  try {
    const savedState = localStorage.getItem(STORAGE_KEY)
    if (savedState) {
      const parsed = JSON.parse(savedState)
      // Load UsersData from localStorage if available
      try {
        const usersData = localStorage.getItem('UsersData')
        const RoleData = localStorage.getItem('RoleData')
        if (usersData) {
          const userData = JSON.parse(usersData)
          parsed.branchId = userData.BranchId || 0
          parsed.OrganizationId = userData.OrganizationId || 0
          parsed.userId = userData.UserId || 0
          parsed.userName = userData.UserName || ''
          parsed.isAdmin = userData.IsAdmin === 1
          parsed.isDepartmentHead = userData.IsDeptHead === 1
          parsed.deptRoleMapId = userData.DeptRoleMapId || 0
          parsed.SubDepartmentId = userData.SubDepartmentId || 0
        }
        if (RoleData) {
          const roleData = JSON.parse(RoleData)

          parsed.deptRoleMapId = roleData.deptRoleMapId || 0
        }
      } catch (e) {
        console.error('Failed to parse UsersData:', e)
      }

      // Ensure user object has all required fields and override branchId with user's value
      if (parsed.user) {
        parsed.user = {
          id: parsed.user.id || 0,
          organizationId: parsed.user.organizationId || 0,
          roleId: parsed.user.roleId || 0,
          email: parsed.user.email || '',
          name: parsed.user.name || '',
          branchId: parsed.user.branchId || 0,
          deptRoleMapId: parsed.user.deptRoleMapId || 0

        }
        // User's branchId takes precedence over UsersData
        parsed.branchId = parsed.user.branchId || parsed.branchId || 0
      }

      return parsed
    }
  } catch (e) {
    console.error('Failed to parse saved auth state:', e)
  }

  return {
    token: null,
    user: null,
    companyId: 0,
    financialyearId: 0,
    entityId: 0,
    isCommon: 0,
    roleId: 0,
    deptRoleMapId: 0,
    OrganizationId: 0,
    financialyearstartdate: null,
    financialyearenddate: null,
    taskEntityId: 0,
    voucherType: 0,
    branchId: 0,
    userId: 0,
    userName: '',
    isAdmin: false,
    isDepartmentHead: false,
    SubDepartmentId: 0,
    activeModule: null,
    companyListLength: 0,
    moceroUIUsersData: null,
    moceroUIRolesData: null
  }
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => loadInitialState(),

  getters: {
    isAuthenticated: (state: AuthState): boolean => !!state.token,
    currentUser: (state: AuthState): User | null => state.user,
    hasModuleAccess: (state: AuthState) => (moduleId: number): boolean => {
      return state.activeModule?.id === moduleId
    },
    hasMenuAccess: (state: AuthState) => (menuId: number): boolean => {
      return state.activeModule?.rights.some((right: MenuRight) =>
        right.MenuId === menuId && right.hasAccess
      ) ?? false
    },
    getMenuOperations: (state: AuthState) => (menuId: number): MenuOperation[] => {
      return state.activeModule?.rights.find((right: MenuRight) =>
        right.MenuId === menuId
      )?.operations ?? []
    },
    activeModuleMenu: (state: AuthState): ModuleMenu => state.activeModule?.menu ?? { menus: [], subMenus: [] }
  },

  actions: {
    setUser(user: User): void {
      this.user = {
        id: user.id,
        organizationId: user.organizationId,
        roleId: user.roleId,
        email: user.email,
        name: user.name,
        branchId: user.branchId,
        deptRoleMapId: user.deptRoleMapId,
        isAdmin: user.isAdmin
      }
      // Assign user's branchId to state's branchId
      this.branchId = user.branchId
      this.userId = user.id
      this.isAdmin = user.isAdmin || false
      this.persistState()
    },
    setToken(token: string): void {
      this.token = token
      this.persistState()
    },
    setCompanyId(id: number): void {
      this.companyId = id
      this.persistState()
    },
    setFinancialYearId(id: number): void {
      this.financialyearId = id
      this.persistState()
    },
    setFinancialYearstartDate(date: any): void {
      this.financialyearstartdate = date
      // alert(this.financialyearstartdate)

      this.persistState()
    },
    setFinancialYearendDate(date: any): void {
      this.financialyearenddate = date
      this.persistState()
    },
    setEntityId(id: number): void {
      this.entityId = id
      this.persistState()
    },
    setIsCommon(isCommon: number): void {
      this.isCommon = isCommon
      // Set isCommon in localStorage with entityId as key

      this.persistState()
    },
    setVoucherType(id: number): void {
      this.voucherType = id
      this.persistState()
    },
    setTaskEntityId(id: number): void {
      this.taskEntityId = id
      this.persistState()
    },
    setRoleId(id: number): void {
      this.roleId = id
      this.persistState()
    },
    setdeptRoleMapId(id: number): void {
      this.deptRoleMapId = id
      this.persistState()
    },

    setOrganizationId(id: number): void {
      this.OrganizationId = id
      this.persistState()
    },
    setBranchId(id: number): void {
      this.branchId = id
      this.persistState()
    },
    setActiveModule(moduleData: ActiveModule | null): void {
      this.activeModule = moduleData
      this.persistState()
    },
    setUserId(id: number): void {
      this.userId = id

      this.persistState()
    },
    setIsAdmin(isAdmin: boolean): void {
      this.isAdmin = isAdmin
      this.persistState()
    },
    setIsDepartmentHead(isDepartmentHead: boolean): void {
      this.isDepartmentHead = isDepartmentHead
      this.persistState()
    },
    setUserName(userName: string): void {
      this.userName = userName
      this.persistState()
    },
    setDeptRoleMapId(deptRoleMapId: number): void {
      this.deptRoleMapId = deptRoleMapId
      this.persistState()
    },
    setSubDepartmentId(SubDepartmentId: number): void {
      this.SubDepartmentId = SubDepartmentId
      this.persistState()
    },
    setUserData(data: { BranchId: number; OrganizationId: number; UserId?: number; UserName?: string; IsAdmin?: number; DeptRoleMapId?: number; IsDeptHead?: number; SubDepartmentId?: number; }): void {
      this.branchId = data.BranchId
      this.OrganizationId = data.OrganizationId
      if (data.UserId !== undefined) {
        this.userId = data.UserId
      }
      if (data.UserName !== undefined) {
        this.userName = data.UserName
      }
      if (data.IsAdmin !== undefined) {
        this.isAdmin = data.IsAdmin === 1
      }
      if (data.DeptRoleMapId !== undefined) {
        this.deptRoleMapId = data.DeptRoleMapId
      }
      if (data.SubDepartmentId !== undefined) {
        this.SubDepartmentId = data.SubDepartmentId
      }
      if (data.IsDeptHead !== undefined) {
        this.isDepartmentHead = data.IsDeptHead === 1
      }
      // Also update localStorage for backward compatibility
      localStorage.setItem('UsersData', JSON.stringify(data))
      this.persistState()
    },
    clearAuth(): void {
      this.token = null
      this.user = null
      this.companyId = 0
      this.financialyearId = 0
      this.entityId = 0
      this.isCommon = 0
      this.voucherType = 0
      this.taskEntityId = 0
      this.roleId = 0
      this.deptRoleMapId = 0
      this.OrganizationId = 0
      this.branchId = 0
      this.userId = 0
      this.userName = ''
      this.isAdmin = false
      this.isDepartmentHead = false
      this.deptRoleMapId = 0
      this.SubDepartmentId = 0
      this.activeModule = null
      localStorage.removeItem(STORAGE_KEY)
      localStorage.removeItem('UsersData') // Also clear UsersData
    },
    setCompanyListLength(length: number): void {
      this.companyListLength = length
      this.persistState()
    },
    setMoceroUIUsersData(userData: any): void {
      this.moceroUIUsersData = userData
      this.persistState()
    },
    setMoceroUIRolesData(rolesData: any): void {
      this.moceroUIRolesData = rolesData
      this.persistState()
    },
    clearMoceroUIData(): void {
      this.moceroUIUsersData = null
      this.moceroUIRolesData = null
      this.persistState()
    },

    persistState(): void {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
          token: this.token,
          user: this.user,
          companyId: this.companyId,
          financialyearId: this.financialyearId,
          financialyearstartdate: this.financialyearstartdate,
          financialyearenddate: this.financialyearenddate,
          taskEntityId: this.taskEntityId,
          entityId: this.entityId,
          isCommon: this.isCommon,
          voucherType: this.voucherType,
          roleId: this.roleId,
          deptRoleMapId: this.deptRoleMapId,
          OrganizationId: this.OrganizationId,
          branchId: this.branchId,
          userId: this.userId,
          userName: this.userName,
          isAdmin: this.isAdmin,
          isDepartmentHead: this.isDepartmentHead,
          SubDepartmentId: this.SubDepartmentId,
          activeModule: this.activeModule,
          companyListLength: this.companyListLength,
          moceroUIUsersData: this.moceroUIUsersData,
          moceroUIRolesData: this.moceroUIRolesData
        }))
      } catch (e) {
        console.error('Failed to persist auth state:', e)
      }
    },
    restoreState(): void {
      // alert(0)
      const state = loadInitialState()
      this.token = state.token
      this.user = state.user
      this.companyId = state.companyId
      this.financialyearId = state.financialyearId
      this.entityId = state.entityId
      this.isCommon = state.isCommon || 0
      this.roleId = state.roleId
      this.deptRoleMapId = state.deptRoleMapId
      this.OrganizationId = state.OrganizationId
      this.branchId = state.branchId
      this.userId = state.userId || 0
      this.userName = state.userName || ''
      this.isAdmin = state.isAdmin || false
      this.isDepartmentHead = state.isDepartmentHead || false
      this.SubDepartmentId = state.SubDepartmentId || 0
      this.activeModule = state.activeModule
      this.companyListLength = state.companyListLength || 0
      this.taskEntityId = state.taskEntityId || 0

    }
  }
})
